import type { TableSortState } from '../../utils/listOrdering';

/** UI column keys for admin orders table (subset are server-sortable). */
export type AdminOrderSortKey =
  | 'id'
  | 'store'
  | 'customer'
  | 'items'
  | 'currency'
  | 'total'
  | 'tax'
  | 'appFee'
  | 'status'
  | 'paid'
  | 'pos'
  | 'externalId'
  | 'paymentId'
  | 'lastWebhook'
  | 'createdAt';

export const ADMIN_ORDER_SORT_KEY_TO_FIELD: Record<
  AdminOrderSortKey,
  string | undefined
> = {
  id: 'id',
  store: 'store_name',
  customer: 'customer_name',
  items: undefined,
  currency: 'currency',
  total: 'total_price',
  tax: 'tax',
  appFee: 'application_fee_cents',
  status: 'status',
  paid: 'is_paid',
  pos: undefined,
  externalId: 'external_order_id',
  paymentId: 'stripe_payment_intent_id',
  lastWebhook: 'last_webhook',
  createdAt: 'created_at',
};

export const ADMIN_ORDER_FIELD_TO_SORT_KEY: Record<string, AdminOrderSortKey> =
  Object.fromEntries(
    Object.entries(ADMIN_ORDER_SORT_KEY_TO_FIELD)
      .filter(([, v]) => v != null)
      .map(([k, v]) => [v as string, k as AdminOrderSortKey]),
  ) as Record<string, AdminOrderSortKey>;

export type AdminCallSortKey =
  | 'id'
  | 'storeName'
  | 'storeId'
  | 'customer'
  | 'direction'
  | 'status'
  | 'fromNumber'
  | 'duration'
  | 'cost'
  | 'sentiment'
  | 'createdAt';

export const ADMIN_CALL_SORT_KEY_TO_FIELD: Record<
  AdminCallSortKey,
  string | undefined
> = {
  id: 'id',
  storeName: 'store_name',
  storeId: 'store_id',
  customer: 'customer_name',
  direction: 'direction',
  status: 'call_status',
  fromNumber: 'from_number',
  duration: 'duration_ms',
  cost: 'combined_cost',
  sentiment: 'user_sentiment',
  createdAt: 'created_at',
};

export const ADMIN_CALL_FIELD_TO_SORT_KEY: Record<string, AdminCallSortKey> =
  Object.fromEntries(
    Object.entries(ADMIN_CALL_SORT_KEY_TO_FIELD)
      .filter(([, v]) => v != null)
      .map(([k, v]) => [v as string, k as AdminCallSortKey]),
  ) as Record<string, AdminCallSortKey>;

export type AdminReservationSortKey =
  | 'storeName'
  | 'storeId'
  | 'updatedAt'
  | 'startTime'
  | 'status'
  | 'confirmationCode'
  | 'reservationId'
  | 'customerId'
  | 'partySize'
  | 'guestName'
  | 'guestPhone'
  | 'guestEmail'
  | 'specialRequest'
  | 'deposit'
  | 'tableType';

export const ADMIN_RESERVATION_SORT_KEY_TO_FIELD: Record<
  AdminReservationSortKey,
  string | undefined
> = {
  storeName: 'store_name',
  storeId: 'store_id',
  updatedAt: 'updated_at',
  startTime: 'start_time',
  status: 'status',
  confirmationCode: 'confirmation_code',
  reservationId: 'reservation_id',
  customerId: 'customer_id',
  partySize: 'party_size',
  guestName: 'guest_first_name',
  guestPhone: 'guest_phone_number',
  guestEmail: 'guest_email',
  specialRequest: 'special_request',
  deposit: 'deposit',
  tableType: 'table_type',
};

export const ADMIN_RESERVATION_FIELD_TO_SORT_KEY: Record<
  string,
  AdminReservationSortKey
> = Object.fromEntries(
  Object.entries(ADMIN_RESERVATION_SORT_KEY_TO_FIELD)
    .filter(([, v]) => v != null)
    .map(([k, v]) => [v as string, k as AdminReservationSortKey]),
) as Record<string, AdminReservationSortKey>;

export const DEFAULT_ADMIN_RESERVATIONS_SORT: TableSortState<AdminReservationSortKey> =
  {
    key: 'updatedAt',
    direction: 'desc',
  };

export type SentNotificationSortKey =
  | 'sentAt'
  | 'store'
  | 'contact'
  | 'channel'
  | 'event'
  | 'status'
  | 'message';

export const SENT_NOTIFICATION_SORT_KEY_TO_FIELD: Record<
  SentNotificationSortKey,
  string
> = {
  sentAt: 'sent_at',
  store: 'store_name',
  contact: 'contact',
  channel: 'channel',
  event: 'event',
  status: 'status',
  message: 'body',
};

export const SENT_NOTIFICATION_FIELD_TO_SORT_KEY: Record<
  string,
  SentNotificationSortKey
> = Object.fromEntries(
  Object.entries(SENT_NOTIFICATION_SORT_KEY_TO_FIELD).map(([key, field]) => [
    field,
    key,
  ]),
) as Record<string, SentNotificationSortKey>;

export const PA_URL_ORDERS = 'pa_orders_ordering';
export const PA_URL_CALLS = 'pa_calls_ordering';
export const PA_URL_RESERVATIONS = 'pa_res_ordering';
export const PA_URL_SENT_NOTIFICATIONS = 'pa_sent_ordering';
